/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@material-ui/styles';
import { TextField, FormControl } from '@material-ui/core';
import clsx from 'clsx';
import {
  preventAlphaInput,
  preventAlphaInputOnPaste,
  removeLeadingZeros,
  filterNumbers,
} from '../../shared/utils/stringUtils';
import AssistiveText from '../submission/templates/AssistiveText';

const useStyles = makeStyles(theme => ({
  root: ({ hasErrors, hasWarnings }) => ({
    '& fieldset': () => ({
      borderColor: `${(hasErrors && theme.palette.error.main) || (hasWarnings && theme.palette.warning.main)}`,
      border: `${(hasErrors && `2px solid ${theme.palette.error.main}`) ||
        (hasWarnings && `2px solid ${theme.palette.warning.main}`) ||
        (!hasWarnings && !hasErrors && `calc(1rem / 16) solid ${theme.palette.input}`)}`,
    }),
    '& .MuiFormLabel-root.Mui-error': {
      color: theme.palette.mainText,
    },
  }),
  customWarning: {
    '& .MuiOutlinedInput-marginDense fieldset': {
      border: `2px solid ${theme.palette.warning.main}`,
    },
    '& .MuiInputLabel-formControl': {
      color: theme.palette.mainText,
    },
  },
  customError: {
    '& .MuiOutlinedInput-marginDense fieldset': {
      border: `2px solid ${theme.palette.error.main}`,
    },
    '& .MuiInputLabel-formControl': {
      color: theme.palette.mainText,
    },
  },
}));

export function NumberField({
  className,
  name,
  label,
  value,
  readOnly,
  errors,
  register,
  type,
  validations,
  extraErrors,
  onChange,
  onBlur,
  disabled,
  required,
  testId,
  hasWarning,
  hasError,
  maxValue,
}) {
  const { t } = useTranslation();
  const hasErrors = !!((errors && errors[name]) || extraErrors) || hasError;
  const helperText = errors && errors[name]?.message;

  const customClasses = useStyles({ hasErrors, hasWarning });
  const classNames = clsx(hasWarning && customClasses.customWarning, hasErrors && customClasses.customError);

  const handleInputChange = (e) => {
    removeLeadingZeros(e);
    filterNumbers(e);

    if (maxValue !== undefined) {
      const parsedValue = Number(e.target.value);
      if (!Number.isNaN(parsedValue) && parsedValue > maxValue) {
        e.target.value = maxValue.toString();
      }
    }

    if (onChange instanceof Function) {
      onChange(e);
    }
  };

  return (
    <FormControl className={clsx(className, customClasses.root)}>
      <>
        <TextField
          className={classNames}
          data-testid={testId}
          required={required}
          name={name}
          id={name}
          variant="outlined"
          label={label}
          value={value}
          type={type}
          error={hasErrors}
          readOnly={readOnly}
          inputRef={register(validations)}
          InputProps={{ inputProps: { min: 0, max: maxValue } }}
          onChange={handleInputChange}
          onBlur={onBlur}
          disabled={disabled}
          onKeyDown={preventAlphaInput}
          onPaste={preventAlphaInputOnPaste}
        />
        {!!helperText && (
          <AssistiveText data-testid="helper-text" severity="error">
            {t(helperText)}
          </AssistiveText>
        )}
      </>
    </FormControl>
  );
}
